import exceptionIcon from '@jetbrains/icons/exception.svg'
import ErrorMessage from '@jetbrains/ring-ui/components/error-message/error-message'
import {Suspense} from 'react'
import type {EntryPointProps} from 'react-relay'
import {usePreloadedQuery} from 'react-relay'

import {getPipelinesHref} from '../../../../routes'
import Loader from '../../../common/Loader/Loader'
import type {PipelinesPagesHeadQuery} from '../__generated__/PipelinesPagesHeadQuery.graphql'
import FullScreenErrorWrapper from '../components/FullScreenErrorWrapper/FullScreenErrorWrapper'
import Link from '../components/Link/Link'
import {usePipelineId} from '../hooks/pipeline'
import {pipelineHeadQuery} from '../PipelinesPages.queries'
import {pipelinesApi} from '../services/pipelinesApi'
import {serializeError} from '../utils/error'

import PipelineHeader from './PipelineHeader/PipelineHeader'
import headerStyles from './PipelineHeader/PipelineHeader.css'
import PipelineHistory from './PipelineHistory/PipelineHistory'
import PipelineLastRun from './PipelineLastRun/PipelineLastRun'

import styles from './PipelinePage.css'

type Queries = {pipelineHead: PipelinesPagesHeadQuery}
type Props = EntryPointProps<Queries, {}, {}, {}>
function PipelinePage({queries}: Props) {
  const {project = null} = usePreloadedQuery(pipelineHeadQuery, queries.pipelineHead)
  const id = usePipelineId()
  const {isLoading, pipelineError} = pipelinesApi.endpoints.getPipelineById.useQuery(id, {
    selectFromResult: ({isLoading, error}) => ({
      isLoading,
      pipelineError: error,
    }),
  })

  if (isLoading) {
    return <Loader />
  }

  if (pipelineError) {
    return (
      <FullScreenErrorWrapper>
        <ErrorMessage
          icon={exceptionIcon}
          message="Error while loading pipeline"
          description={serializeError(pipelineError)}
        >
          <Link to={getPipelinesHref()}>{'Go to the home page'}</Link>
        </ErrorMessage>
      </FullScreenErrorWrapper>
    )
  }

  return (
    <>
      <PipelineHeader pipelineHeadKey={project} />
      <div className={styles.content}>
        <PipelineLastRun pipelineHeadKey={project} />
        <PipelineHistory pipelineHeadKey={project} />
      </div>
    </>
  )
}

export default function PipelinePageContainer(props: Props) {
  return (
    <div className={styles.page}>
      <Suspense fallback={<Loader className={headerStyles.header} />}>
        <PipelinePage {...props} />
      </Suspense>
    </div>
  )
}

export type PipelinePageComponent = typeof PipelinePage
